// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { Menu, Typography, List, ListItemButton, Button, InputAdornment, TextField, IconButton, SvgIcon } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import Search from '@mui/icons-material/Search';
import { useAppDispatch, useAppSelector } from 'Hooks';
import LinePropertiesFlyout from './LinePropertiesFlyout/index';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

interface ComponentProps {
  layers?: IVectorTileJson;
  order: IOrder;
  siteId: string;
  limitTo?: number;
  onRequestShowAll?: () => void;
  layerType: 'underground' | 'aerial';
  checkedLayers: Record<string, boolean>; // Controlled state from parent
  onToggleLayer: (layerId: string, forceState?: boolean) => void; // Callback to parent
}

export const ProjectLayersList: React.FC<ComponentProps> = ({
  order,
  layers,
  siteId,
  limitTo,
  onRequestShowAll,
  layerType,
  checkedLayers,
  onToggleLayer,
}) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const resources = useAppSelector((state) => state.auth.resources);
  const editorFunctions = 'editorFunctions' in resources;
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);
  const [filteredLayers, setFilteredLayers] = useState(layers.vector_layers);
  const [searchQuery, setSearchQuery] = useState('');

  const isShowingAll = limitTo === undefined;
  const layersToDisplay = useMemo(() => {
    if (!filteredLayers || filteredLayers.length === 0) return [];
    return isShowingAll
      ? filteredLayers
      : filteredLayers.slice(0, limitTo ?? 5);
  }, [filteredLayers, isShowingAll, limitTo]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === '') {
      setFilteredLayers(layers?.vector_layers || []);
    } else {
      const filtered = (layers?.vector_layers || []).filter((layer) =>
        layer.custom_name?.toLowerCase().includes(query) ||
        layer.id.toLowerCase().includes(query));
      setFilteredLayers(filtered);
    }
  };

  useEffect(() => {
    setFilteredLayers(layers.vector_layers);
  }, [layers?.vector_layers, siteId]);

  const handleSelectAll = () => {
    const allSelected = layersToDisplay.every((layer) => checkedLayers[layer.id]);
    const newState = !allSelected;

    layersToDisplay.forEach((layer) => {
      onToggleLayer(layer.id, newState);
    });
  };

  const handleToggleProjectLayer = useCallback((layerId: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onToggleLayer(layerId);
  }, [onToggleLayer]);

  const allLayersDeselected = layersToDisplay.every((layer) => !checkedLayers[layer.id]);
  const someLayersSelected = layersToDisplay.some((layer) => checkedLayers[layer.id]);

  return (
    <Box sx={styles.listWrapper}>
      <Box sx={styles.projectLayersText}>
        <Button variant="text" sx={styles.deselectButton} onClick={handleSelectAll}>
          {allLayersDeselected || !someLayersSelected ? 'Select All' : 'Deselect All'}
        </Button>
      </Box>

      <List>
        <TextField
          size="small"
          variant="outlined"
          sx={styles.searchInput}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          placeholder="Search layers"
          onChange={handleSearch}
          value={searchQuery}
        />
        {layersToDisplay.map((layer) => (
          <Box key={layer.layerId}>
            <ListItemButton
              sx={styles.listItem}
            >
              <LinePropertiesFlyout layer={layer} siteId={siteId} />
                <Typography variant="h5" sx={styles.listItemText}
                noWrap
                title={layer.custom_name ?? layer.id}
              >
                  {layer.custom_name ?? layer.id}
                </Typography>
              {/* Eye Toggle Icon */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleToggleProjectLayer(layer.id, e);
                }}
                sx={styles.eyetoggleopac}
              >
                <SvgIcon fontSize="small">
                  {checkedLayers[layer.id] ? (
                    // eye-open
                    <path d="M10 4.1665C5.90909 4.1665 2.41545 6.71105 1 10.3029C2.41545 13.8947 5.90909 16.4392 10 16.4392C14.0909 16.4392 17.5845 13.8947 19 10.3029C17.5845 6.71105 14.0909 4.1665 10 4.1665ZM10 14.3938C7.74182 14.3938 5.90909 12.561 5.90909 10.3029C5.90909 8.04469 7.74182 6.21196 10 6.21196C12.2582 6.21196 14.0909 8.04469 14.0909 10.3029C14.0909 12.561 12.2582 14.3938 10 14.3938ZM10 7.84832C8.64182 7.84832 7.54545 8.94469 7.54545 10.3029C7.54545 11.661 8.64182 12.7574 10 12.7574C11.3582 12.7574 12.4545 11.661 12.4545 10.3029C12.4545 8.94469 11.3582 7.84832 10 7.84832Z" />
                  ) : (
                    // eye-closed
                    <path d="M19.5872 1.06055L15.0764 5.57031C16.7934 6.66308 18.1433 8.283 18.8997 10.2021C17.4842 13.7939 13.9905 16.3388 9.89966 16.3389C8.27973 16.3389 6.754 15.9384 5.41333 15.2334L1.06079 19.5869L0.212158 18.7383L4.35864 14.5908C2.81491 13.5073 1.60167 11.9836 0.899658 10.2021C2.31516 6.61043 5.80882 4.06641 9.89966 4.06641C11.3582 4.06643 12.7402 4.39068 13.9797 4.96973L18.7385 0.211914L19.5872 1.06055ZM11.885 8.76172C12.1787 9.16626 12.3537 9.66269 12.3538 10.2021C12.3538 11.5603 11.2578 12.6572 9.89966 12.6572C9.36009 12.6572 8.86285 12.4822 8.45825 12.1885L7.29321 13.3535C8.00122 13.9399 8.90925 14.293 9.89966 14.293C12.1578 14.2929 13.9905 12.4603 13.9905 10.2021C13.9904 9.21179 13.6374 8.30367 13.051 7.5957L11.885 8.76172ZM9.89966 6.11133C7.64153 6.11133 5.80892 7.94404 5.80884 10.2021C5.80884 11.0367 6.0599 11.8127 6.4895 12.46L7.68677 11.2627C7.53306 10.9417 7.44458 10.5826 7.44458 10.2021C7.44467 8.84404 8.54153 7.74805 9.89966 7.74805C10.2799 7.74807 10.6383 7.83663 10.9592 7.99023L12.1575 6.79199C11.5102 6.36239 10.7342 6.11136 9.89966 6.11133Z" />
                  )}
                </SvgIcon>
              </IconButton>
            </ListItemButton>
          </Box>
        ))}
        {/* Toggle Button: VIEW ALL LAYERS ↔ SHOW LESS */}
        {layers?.vector_layers && layers.vector_layers.length > 5 && (
          <Box sx={styles.viewalllayerbox}>
            <Typography
              variant="caption"
              sx={styles.visiblealllayers}
              onClick={() => onRequestShowAll?.()}
            >
              {isShowingAll ? 'SHOW LESS' : 'VIEW ALL LAYERS'}
            </Typography>
          </Box>
        )}
      </List>
    </Box>
  );
};

export default ProjectLayersList;
